import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Add, Assignment, Clear, Delete, Edit, Link, MoreVert, Search } from "@material-ui/icons"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { useSnackbar } from "notistack"
import React, { useMemo, useState } from "react"
import {
    atualizarFichaTecnica,
    deletarFichaTecnica,
    salvarFichaTecnica,
    vinculosFichaTecnica,
} from "../../../services/fichaTecnica"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import DialogFTI from "./DialogFTI"

const useStyles = makeStyles(theme => ({
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    tableContainer: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
    },
    tableHeader: {
        backgroundColor: "#f8f9fa",
        "& .MuiTableCell-head": {
            fontWeight: 600,
            color: "#2c3e50",
            borderBottom: "2px solid #e9ecef",
        },
    },
    tableRow: {
        transition: "all 0.2s ease",
        "&:hover": { backgroundColor: "#f8f9fa" },
        "& .MuiTableCell-root": {
            borderBottom: "1px solid #f1f3f4",
            padding: theme.spacing(2),
        },
    },
    searchContainer: {
        marginBottom: theme.spacing(3),
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
    },
    searchField: {
        backgroundColor: "white",
        "& .MuiOutlinedInput-root": {
            borderRadius: 25,
            "& fieldset": { border: "2px solid #e9ecef" },
            "&:hover fieldset": { border: "2px solid #4caf50" },
            "&.Mui-focused fieldset": { border: "2px solid #4caf50" },
        },
    },
    addButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 3),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        "&:hover": {
            backgroundColor: "#45a049",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
        },
    },
    insumoChip: {
        margin: theme.spacing(0.25),
        borderRadius: 15,
        backgroundColor: "#e3f2fd",
        color: "#1976d2",
        fontSize: "0.75rem",
    },
    produtoChip: {
        margin: theme.spacing(0.25),
        borderRadius: 15,
        backgroundColor: "#e8f5e8",
        color: "#2e7d32",
        fontSize: "0.75rem",
    },
    linkButton: {
        borderRadius: 15,
        color: "#4caf50",
        border: "1px solid #4caf50",
        "&:hover": { backgroundColor: "#f1f8e9" },
    },
    dialogContent: { padding: theme.spacing(3) },
    formField: {
        marginBottom: theme.spacing(2),
        "& .MuiOutlinedInput-root": { borderRadius: 15 },
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(8),
        color: "#95a5a6",
    },
    iconHeader: { color: "#4caf50", marginRight: theme.spacing(1) },
    paginationContainer: {
        backgroundColor: "white",
        borderTop: "1px solid #f1f3f4",
        padding: theme.spacing(1),
    },
}))

const unidades = { U: "u", G: "g", KG: "Kg", L: "l", ML: "Ml" }

const numero = v => {
    if (v == null) return 0
    if (typeof v === "number") return v
    if (typeof v === "string") return Number(v.replace(",", ".")) || 0
    return Number(v) || 0
}

export default function FichaTecnica({
    FTEstabelecimento,
    setFTEstabelecimento,
    insumos,
    cardapio,
}) {
    const classes = useStyles()
    const configs = useAppStore(s => s.configuracoes)
    const idEstabelecimentoAtual = configs?.user?.parametros?.estabelecimento?.id
    const autorizacao = localStorage.getItem("chaveSistema")
    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()

    const produtos =
        cardapio?.categorias?.reduce((acc, categoria) => {
            if (categoria.produtos && Array.isArray(categoria.produtos)) {
                const produtosDaCategoria = categoria.produtos.map(produto => ({
                    id: produto.id,
                    nome: produto.nome,
                    categoria: categoria.nome,
                    codigo: produto.codigo,
                    descricao: produto.descricao,
                    ativo: produto.ativo,
                }))
                return [...acc, ...produtosDaCategoria]
            }
            return acc
        }, []) || []

    const extractOpcoes = (produto, categoria) => {
        const lista = []
        const arr = Array.isArray(produto?.opcoes) ? produto.opcoes : []

        arr.forEach(opcao => {
            if (Array.isArray(opcao?.opcoes) && opcao.opcoes.length) {
                opcao.opcoes.forEach(sub => {
                    lista.push({
                        id: sub.id,
                        nome: sub.nome,
                        codigo: sub.codigo,
                        descricao: sub.descricao,
                        ativo: sub.ativo,
                        produto: produto.nome,
                        produtoId: produto.id,
                        categoria: categoria.nome,
                        adicional: sub.adicional,
                        considerarMaiorValor: sub.considerarMaiorValor,
                        considerarMedia: sub.considerarMedia,
                        grupoId: opcao.id,
                        grupoNome: opcao.nome,
                    })
                })
            } else {
                lista.push({
                    id: opcao.id,
                    nome: opcao.nome,
                    codigo: opcao.codigo,
                    descricao: opcao.descricao,
                    ativo: opcao.ativo,
                    produto: produto.nome,
                    produtoId: produto.id,
                    categoria: categoria.nome,
                    adicional: opcao.adicional,
                    considerarMaiorValor: opcao.considerarMaiorValor,
                    considerarMedia: opcao.considerarMedia,
                })
            }
        })

        return lista
    }

    const opcoes =
        cardapio?.categorias?.flatMap(categoria =>
            (categoria?.produtos ?? []).flatMap(produto => extractOpcoes(produto, categoria))
        ) ?? []

    const produtoById = useMemo(() => new Map(produtos.map(p => [p.id, p])), [produtos])
    const opcaoById = useMemo(() => new Map(opcoes.map(o => [o.id, o])), [opcoes])
    const resolveProdutos = (ids = [], map) => ids.map(id => map.get(id)).filter(Boolean)
    const resolveOpcoes = (ids = [], map) => ids.map(id => map.get(id)).filter(Boolean)

    const categoriasDoEstabelecimento = useMemo(() => {
        const nomes = (cardapio?.categorias ?? []).map(c => c?.nome).filter(Boolean)
        return Array.from(new Set(nomes)).sort()
    }, [cardapio])

    const [busca, setBusca] = useState("")
    const [openDialog, setOpenDialog] = useState(false)
    const [openDialogProdutos, setOpenDialogProdutos] = useState(false)
    const [fichaParaVincular, setFichaParaVincular] = useState(null)
    const [tabValue, setTabValue] = useState(0)
    const [buscaProdutos, setBuscaProdutos] = useState("")
    const [buscaOpcoes, setBuscaOpcoes] = useState("")
    const [filtroCategorias, setFiltroCategorias] = useState([])
    const [fichaEditando, setFichaEditando] = useState(null)
    const [anchorEl, setAnchorEl] = useState(null)
    const [fichaSelecionada, setFichaSelecionada] = useState(null)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [openDialogInsumo, setOpenDialogInsumo] = useState(false)
    const [nome, setNome] = useState("")
    const [descricao, setDescricao] = useState("")
    const [insumosSelecionados, setInsumosSelecionados] = useState([])

    const alertStart = (msg, tipo) => enqueueSnackbar(msg, { variant: tipo })

    const fichasFiltradas = FTEstabelecimento?.filter(
        ficha =>
            ficha.nome?.toLowerCase().includes(busca?.toLowerCase()) ||
            ficha.descricao?.toLowerCase().includes(busca?.toLowerCase())
    )

    const handleMenuOpen = (event, ficha) => {
        setAnchorEl(event.currentTarget)
        setFichaSelecionada(ficha)
    }
    const handleMenuClose = () => {
        setAnchorEl(null)
        setFichaSelecionada(null)
    }
    const handleChangePage = (_, newPage) => setPage(newPage)
    const handleChangeRowsPerPage = event => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    const abrirDialogNovo = () => {
        setFichaEditando(null)
        setNome("")
        setDescricao("")
        setInsumosSelecionados([])
        setOpenDialog(true)
    }

    const abrirDialogEditar = ficha => {
        setFichaEditando(ficha)
        setNome(ficha.nome)
        setDescricao(ficha.descricao)
        setInsumosSelecionados(ficha.insumos)
        setOpenDialog(true)
        handleMenuClose()
    }

    const excluirFicha = async ficha => {
        setLoading(true)
        try {
            const response = await deletarFichaTecnica(
                idEstabelecimentoAtual,
                ficha.id,
                autorizacao
            )
            if (response.retornoErro === 1) return alertStart(response.mensagem, "error")

            setFTEstabelecimento(prev => prev.filter(f => f.id !== ficha.id))
            alertStart("Ficha técnica excluída com sucesso", "success")
            handleMenuClose()
        } catch (error) {
            alertStart(`Erro: ${error}`, "error")
        } finally {
            setLoading(false)
        }
    }

    const salvarFicha = async () => {
        setLoading(true)
        try {
            if (!nome.trim()) {
                alertStart("Nome é obrigatório", "error")
                return
            }

            const insumoEnviar = insumosSelecionados.map(i => ({
                id: i.id,
                quantidade: i.quantidade,
            }))

            if (fichaEditando) {
                const fichaAtualizada = { nome, descricao, insumos: insumoEnviar }
                const response = await atualizarFichaTecnica(
                    idEstabelecimentoAtual,
                    fichaEditando.id,
                    fichaAtualizada,
                    autorizacao
                )
                if (response.retornoErro === 1) return alertStart(response.mensagem, "error")

                setFTEstabelecimento(prev =>
                    prev.map(f =>
                        f.id === fichaEditando.id
                            ? { ...f, nome, descricao, insumos: insumosSelecionados }
                            : f
                    )
                )
                alertStart("Ficha técnica atualizada com sucesso", "success")
            } else {
                const novaFicha = { nome, descricao, insumos: insumoEnviar }
                const response = await salvarFichaTecnica(
                    idEstabelecimentoAtual,
                    novaFicha,
                    autorizacao
                )
                if (response.retornoErro === 1) return alertStart(response.mensagem, "error")

                setFTEstabelecimento(prev => [...prev, response])
                alertStart("Ficha técnica criada com sucesso", "success")
            }
        } catch (error) {
            alertStart(`Erro: ${error}`, "error")
        } finally {
            setLoading(false)
            setOpenDialog(false)
        }
    }

    const adicionarInsumo = novoInsumo => setInsumosSelecionados(prev => [...prev, novoInsumo])

    const produtosFiltrados = produtos.filter(produto => {
        const termo = buscaProdutos.toLowerCase()
        const bateBusca =
            produto.nome?.toLowerCase().includes(termo) ||
            produto.categoria?.toLowerCase().includes(termo) ||
            produto.codigo?.toLowerCase().includes(termo) ||
            produto.descricao?.toLowerCase().includes(termo)

        const bateCategoria =
            filtroCategorias.length === 0 || filtroCategorias.includes(produto.categoria)
        return bateBusca && bateCategoria
    })

    const opcoesFiltradas = opcoes.filter(opcao => {
        const termo = buscaOpcoes.toLowerCase()
        const bateBusca =
            opcao.nome?.toLowerCase().includes(termo) ||
            opcao.produto?.toLowerCase().includes(termo) ||
            opcao.categoria?.toLowerCase().includes(termo) ||
            opcao.codigo?.toLowerCase().includes(termo) ||
            opcao.descricao?.toLowerCase().includes(termo)

        const bateCategoria =
            filtroCategorias.length === 0 || filtroCategorias.includes(opcao.categoria)
        return bateBusca && bateCategoria
    })

    const vincularProdutos = async () => {
        setLoading(true)
        try {
            const data = {
                fichaTecnicaId: fichaParaVincular?.id,
                opcaoIds: (fichaParaVincular?.opcoes || []).map(o => o.id),
                produtoIds: (fichaParaVincular?.produtos || []).map(p => p.id),
            }

            const response = await vinculosFichaTecnica(idEstabelecimentoAtual, data, autorizacao)
            if (response.retornoErro === 1) {
                alertStart(response.mensagem, "error")
                return
            }

            setFTEstabelecimento(prev =>
                prev.map(f =>
                    f.id === fichaParaVincular.id
                        ? {
                              ...f,
                              vinculos: {
                                  ...(f.vinculos || {}),
                                  produto_ids: data.produtoIds,
                                  opcaoIds: data.opcaoIds,
                              },
                          }
                        : f
                )
            )

            alertStart("Vinculações salvas com sucesso", "success")
        } catch (error) {
            alertStart(`Erro: ${error}`, "error")
        } finally {
            setLoading(false)
        }
    }

    const total = insumosSelecionados.reduce((acc, item) => {
        const unit = numero(item.valor_unitario ?? item.estoque?.valor_unitario)
        const qty = numero(item.quantidade ?? item.estoque?.quantidade)
        return acc + unit * qty
    }, 0)

    const totalBRL = total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    const precoSugerido = (total / 0.3).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    })

    return (
        <Box style={{ marginBottom: "1.5em" }}>
            <Box className={classes.header}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h4" style={{ fontWeight: 600, color: "#2c3e50" }}>
                        Fichas Técnicas
                    </Typography>
                </Box>
                <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                    {FTEstabelecimento?.length} fichas cadastradas
                </Typography>
            </Box>

            <Box className={classes.searchContainer}>
                <TextField
                    className={classes.searchField}
                    placeholder="Buscar fichas técnicas..."
                    variant="outlined"
                    size="small"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search style={{ color: "#95a5a6" }} />
                            </InputAdornment>
                        ),
                    }}
                    style={{ flex: 1 }}
                />
                <Button className={classes.addButton} startIcon={<Add />} onClick={abrirDialogNovo}>
                    Nova Ficha
                </Button>
            </Box>

            {fichasFiltradas?.length > 0 ? (
                <Paper className={classes.tableContainer}>
                    <TableContainer>
                        <Table>
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    <TableCell style={{ width: "20%" }}>Nome</TableCell>
                                    <TableCell style={{ width: "25%" }}>Descrição</TableCell>
                                    <TableCell style={{ width: "25%" }}>Insumos</TableCell>
                                    <TableCell style={{ width: "20%" }}>Vinculações</TableCell>
                                    <TableCell style={{ width: "10%" }} align="center">
                                        Ações
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fichasFiltradas
                                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(ficha => (
                                        <TableRow key={ficha.id} className={classes.tableRow}>
                                            <TableCell>
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 600,
                                                        color: "#2c3e50",
                                                        maxWidth: "200px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        textWrap: "nowrap",
                                                        cursor: "default",
                                                    }}
                                                    title={ficha.nome}
                                                >
                                                    {ficha.nome}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d" }}
                                                >
                                                    {ficha.descricao || "Sem descrição"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    {ficha.insumos.slice(0, 3).map(insumo => (
                                                        <Chip
                                                            key={insumo.id}
                                                            label={`${insumo.nome.slice(0, 25)} ${
                                                                insumo.nome.length > 25 ? "..." : ""
                                                            }`}
                                                            size="small"
                                                            className={classes.insumoChip}
                                                            title={insumo.nome}
                                                        />
                                                    ))}
                                                    {ficha.insumos.length > 3 && (
                                                        <Chip
                                                            label={`+${ficha.insumos.length - 3}`}
                                                            size="small"
                                                            className={classes.insumoChip}
                                                            style={{
                                                                backgroundColor: "#f0f0f0",
                                                                color: "#666",
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Box
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.5em",
                                                    }}
                                                >
                                                    <Box>
                                                        {(() => {
                                                            const idsProdutos =
                                                                ficha?.vinculos?.produto_ids ?? []
                                                            const idsOpcoes =
                                                                ficha?.vinculos?.opcaoIds ?? []

                                                            const produtosVinculados =
                                                                resolveProdutos(
                                                                    idsProdutos,
                                                                    produtoById
                                                                )
                                                            const opcoesVinculadas = resolveOpcoes(
                                                                idsOpcoes,
                                                                opcaoById
                                                            )

                                                            const totalCount =
                                                                produtosVinculados.length +
                                                                opcoesVinculadas.length
                                                            const temAlgo = totalCount > 0

                                                            const shownProdutos =
                                                                produtosVinculados.slice(0, 2)
                                                            const remainingSlots = Math.max(
                                                                0,
                                                                2 - shownProdutos.length
                                                            )
                                                            const shownOpcoes =
                                                                remainingSlots > 0
                                                                    ? opcoesVinculadas.slice(
                                                                          0,
                                                                          remainingSlots
                                                                      )
                                                                    : []

                                                            const shownCount =
                                                                shownProdutos.length +
                                                                shownOpcoes.length
                                                            const extraCount = Math.max(
                                                                0,
                                                                totalCount - shownCount
                                                            )

                                                            if (!temAlgo) {
                                                                return (
                                                                    <Typography
                                                                        variant="body2"
                                                                        style={{
                                                                            color: "#95a5a6",
                                                                            fontStyle: "italic",
                                                                        }}
                                                                    >
                                                                        Nenhuma vinculação
                                                                    </Typography>
                                                                )
                                                            }

                                                            return (
                                                                <>
                                                                    {shownProdutos.map(prod => (
                                                                        <Chip
                                                                            key={`produto-${prod.id}`}
                                                                            label={`P: ${
                                                                                prod.nome
                                                                            }${
                                                                                prod.categoria
                                                                                    ? ` (${prod.categoria})`
                                                                                    : ""
                                                                            }`}
                                                                            size="small"
                                                                            className={
                                                                                classes.produtoChip
                                                                            }
                                                                        />
                                                                    ))}
                                                                    {shownOpcoes.map(op => (
                                                                        <Chip
                                                                            key={`opcao-${op.id}`}
                                                                            label={`O: ${op.nome}${
                                                                                op.produto
                                                                                    ? ` (${op.produto})`
                                                                                    : ""
                                                                            }`}
                                                                            size="small"
                                                                            style={{
                                                                                margin: 2,
                                                                                borderRadius: 15,
                                                                                backgroundColor:
                                                                                    "#fff3e0",
                                                                                color: "#f57c00",
                                                                                fontSize: "0.75rem",
                                                                            }}
                                                                        />
                                                                    ))}
                                                                    {extraCount > 0 && (
                                                                        <Chip
                                                                            label={`+${extraCount}`}
                                                                            size="small"
                                                                            style={{
                                                                                margin: 2,
                                                                                borderRadius: 15,
                                                                                backgroundColor:
                                                                                    "#f0f0f0",
                                                                                color: "#666",
                                                                                fontSize: "0.75rem",
                                                                            }}
                                                                        />
                                                                    )}
                                                                </>
                                                            )
                                                        })()}
                                                    </Box>

                                                    <IconButton
                                                        size="small"
                                                        className={classes.linkButton}
                                                        onClick={() => {
                                                            const produtosSelecionados =
                                                                resolveProdutos(
                                                                    ficha?.vinculos?.produto_ids ||
                                                                        [],
                                                                    produtoById
                                                                )
                                                            const opcoesSelecionadas =
                                                                resolveOpcoes(
                                                                    ficha?.vinculos?.opcaoIds || [],
                                                                    opcaoById
                                                                )

                                                            setFichaParaVincular({
                                                                ...ficha,
                                                                produtos: produtosSelecionados,
                                                                opcoes: opcoesSelecionadas,
                                                            })
                                                            setTabValue(0)
                                                            setOpenDialogProdutos(true)
                                                        }}
                                                        title="Vincular produtos e opções"
                                                    >
                                                        <Link fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>

                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={e => handleMenuOpen(e, ficha)}
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box className={classes.paginationContainer}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={fichasFiltradas?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Itens por página"
                        />
                    </Box>
                </Paper>
            ) : (
                <Box className={classes.emptyState}>
                    <Assignment style={{ fontSize: 80, marginBottom: 16 }} />
                    <Typography variant="h6">Nenhuma ficha técnica encontrada</Typography>
                    <Typography variant="body2">
                        {busca
                            ? "Tente ajustar sua busca"
                            : "Comece criando sua primeira ficha técnica"}
                    </Typography>
                </Box>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                keepMounted
            >
                <MenuItem onClick={() => abrirDialogEditar(fichaSelecionada)}>
                    <Edit style={{ marginRight: 8 }} />
                    Editar
                </MenuItem>
                <MenuItem onClick={() => excluirFicha(fichaSelecionada)}>
                    <Delete style={{ marginRight: 8 }} />
                    Excluir
                </MenuItem>
            </Menu>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: 20 } }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        {fichaEditando ? "Editar Ficha Técnica" : "Nova Ficha Técnica"}
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <TextField
                        fullWidth
                        label="Nome da ficha técnica"
                        variant="outlined"
                        value={nome}
                        onChange={e => setNome(e.target.value)}
                        className={classes.formField}
                        placeholder="Ex: Hambúrguer Artesanal"
                    />
                    <TextField
                        fullWidth
                        label="Descrição"
                        variant="outlined"
                        multiline
                        minRows={3}
                        value={descricao}
                        onChange={e => setDescricao(e.target.value)}
                        className={classes.formField}
                        placeholder="Descreva os detalhes da preparação..."
                    />

                    <Typography variant="subtitle1" style={{ marginBottom: 16, color: "#34495e" }}>
                        Insumos Selecionados ({insumosSelecionados.length})
                    </Typography>

                    {insumosSelecionados.length > 0 ? (
                        <Box>
                            {insumosSelecionados.map(insumo => (
                                <Chip
                                    key={insumo.id}
                                    label={`${
                                        Number(insumo.quantidade).toFixed(2).replace(".", ",") +
                                        "" +
                                        unidades[insumo.unidade_medida]
                                    } - ${insumo.nome} - ${(
                                        +insumo.valor_unitario * +insumo.quantidade
                                    ).toLocaleString("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}`}
                                    onDelete={() =>
                                        setInsumosSelecionados(prev =>
                                            prev.filter(i => i.id !== insumo.id)
                                        )
                                    }
                                    style={{ margin: 4 }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            style={{ color: "#95a5a6", fontStyle: "italic" }}
                        >
                            Nenhum insumo selecionado ainda
                        </Typography>
                    )}

                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        style={{ marginTop: 16, borderRadius: 15 }}
                        onClick={() => setOpenDialogInsumo(true)}
                    >
                        Adicionar Insumo
                    </Button>
                </DialogContent>
                <DialogActions
                    style={{
                        padding: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="subtitle1" style={{ color: "#34495e" }}>
                        Custo {totalBRL} - Preço sugerido {precoSugerido}
                    </Typography>
                    <Box style={{ display: "flex", gap: "0.5em" }}>
                        <Button onClick={() => setOpenDialog(false)} style={{ borderRadius: 15 }}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={salvarFicha}
                            variant="contained"
                            style={{ borderRadius: 15, backgroundColor: "#4caf50", color: "white" }}
                        >
                            {fichaEditando ? "Atualizar" : "Criar"}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <DialogFTI
                insumos={insumos}
                open={openDialogInsumo}
                onClose={() => setOpenDialogInsumo(false)}
                adicionarInsumo={adicionarInsumo}
                insumosSelecionados={insumosSelecionados}
            />

            <Dialog
                open={openDialogProdutos}
                onClose={() => {
                    setOpenDialogProdutos(false)
                    setBuscaProdutos("")
                    setBuscaOpcoes("")
                    setFiltroCategorias([])
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: 20 } }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Link style={{ marginRight: 8, color: "#4caf50" }} />
                        Vincular à Ficha Técnica
                    </Box>
                    {fichaParaVincular && (
                        <Typography variant="subtitle2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                            {fichaParaVincular.nome}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, newValue) => setTabValue(newValue)}
                        indicatorColor="primary"
                        textColor="primary"
                        style={{ marginBottom: 16 }}
                    >
                        <Tab label={`Produtos (${produtosFiltrados.length}/${produtos.length})`} />
                        <Tab label={`Opções (${opcoesFiltradas.length}/${opcoes.length})`} />
                    </Tabs>

                    {tabValue === 0 && (
                        <>
                            <Typography
                                variant="subtitle1"
                                style={{ marginBottom: 16, color: "#34495e" }}
                            >
                                Selecione os produtos que utilizarão esta ficha técnica:
                            </Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={6} md={6} lg={6}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar produtos por nome, categoria, código..."
                                        variant="outlined"
                                        size="small"
                                        value={buscaProdutos}
                                        onChange={e => setBuscaProdutos(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search style={{ color: "#95a5a6" }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: buscaProdutos && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setBuscaProdutos("")}
                                                    >
                                                        <Clear fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        style={{
                                            marginBottom: 16,
                                            backgroundColor: "white",
                                            borderRadius: 15,
                                        }}
                                        className={classes.formField}
                                    />
                                </Grid>
                                <Grid item xs={6} md={6} lg={6}>
                                    <Autocomplete
                                        multiple
                                        options={categoriasDoEstabelecimento}
                                        value={filtroCategorias}
                                        onChange={(_, value) => setFiltroCategorias(value)}
                                        filterSelectedOptions
                                        size="small"
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="default"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    style={{ borderRadius: 14 }}
                                                />
                                            ))
                                        }
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Filtrar por categorias"
                                                placeholder="Selecione categorias"
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {tabValue === 1 && (
                        <>
                            <Typography
                                variant="subtitle1"
                                style={{ marginBottom: 16, color: "#34495e" }}
                            >
                                Selecione as opções que utilizarão esta ficha técnica:
                            </Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={6} md={6} lg={6}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar opções por nome, produto, categoria, código..."
                                        variant="outlined"
                                        size="small"
                                        value={buscaOpcoes}
                                        onChange={e => setBuscaOpcoes(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search style={{ color: "#95a5a6" }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: buscaOpcoes && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setBuscaOpcoes("")}
                                                    >
                                                        <Clear fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        style={{
                                            marginBottom: 16,
                                            backgroundColor: "white",
                                            borderRadius: 15,
                                        }}
                                        className={classes.formField}
                                    />
                                </Grid>
                                <Grid item xs={6} md={6} lg={6}>
                                    <Autocomplete
                                        multiple
                                        options={categoriasDoEstabelecimento}
                                        value={filtroCategorias}
                                        onChange={(_, value) => setFiltroCategorias(value)}
                                        filterSelectedOptions
                                        size="small"
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="default"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    style={{ borderRadius: 14 }}
                                                />
                                            ))
                                        }
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Filtrar por categorias"
                                                placeholder="Selecione categorias"
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {tabValue === 0 &&
                        (produtosFiltrados.length === 0 ? (
                            <Typography
                                variant="body2"
                                style={{
                                    color: "#95a5a6",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    padding: 32,
                                }}
                            >
                                {buscaProdutos || filtroCategorias.length
                                    ? "Nenhum produto encontrado com esses filtros"
                                    : "Nenhum produto encontrado no cardápio"}
                            </Typography>
                        ) : (
                            <Box>
                                {produtosFiltrados.map(produto => {
                                    const isSelected =
                                        fichaParaVincular?.produtos?.some(
                                            p => p.id === produto.id
                                        ) || false
                                    return (
                                        <Chip
                                            key={produto.id}
                                            label={`${produto.nome} ${
                                                produto.categoria ? `(${produto.categoria})` : ""
                                            }`}
                                            clickable
                                            color={isSelected ? "primary" : "default"}
                                            onClick={() => {
                                                const produtosAtuais =
                                                    fichaParaVincular?.produtos || []
                                                const novosProdutos = isSelected
                                                    ? produtosAtuais.filter(
                                                          p => p.id !== produto.id
                                                      )
                                                    : [...produtosAtuais, produto]
                                                setFichaParaVincular(prev => ({
                                                    ...prev,
                                                    produtos: novosProdutos,
                                                }))
                                            }}
                                            style={{
                                                margin: 4,
                                                borderRadius: 15,
                                                backgroundColor: isSelected ? "#4caf50" : "#f5f5f5",
                                                color: isSelected ? "white" : "#666",
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        ))}

                    {tabValue === 1 &&
                        (opcoesFiltradas.length === 0 ? (
                            <Typography
                                variant="body2"
                                style={{
                                    color: "#95a5a6",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    padding: 32,
                                }}
                            >
                                {buscaOpcoes || filtroCategorias.length
                                    ? "Nenhuma opção encontrada com esses filtros"
                                    : "Nenhuma opção encontrada no cardápio"}
                            </Typography>
                        ) : (
                            <Box>
                                {opcoesFiltradas.map(opcao => {
                                    const isSelected =
                                        fichaParaVincular?.opcoes?.some(o => o.id === opcao.id) ||
                                        false
                                    return (
                                        <Chip
                                            key={opcao.id}
                                            label={`${opcao.nome} (${opcao.produto} - ${opcao.categoria})`}
                                            clickable
                                            color={isSelected ? "primary" : "default"}
                                            onClick={() => {
                                                const opcoesAtuais = fichaParaVincular?.opcoes || []
                                                const novasOpcoes = isSelected
                                                    ? opcoesAtuais.filter(o => o.id !== opcao.id)
                                                    : [...opcoesAtuais, opcao]
                                                setFichaParaVincular(prev => ({
                                                    ...prev,
                                                    opcoes: novasOpcoes,
                                                }))
                                            }}
                                            style={{
                                                margin: 4,
                                                borderRadius: 15,
                                                backgroundColor: isSelected ? "#f57c00" : "#f5f5f5",
                                                color: isSelected ? "white" : "#666",
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        ))}

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginTop={2}
                    >
                        <Typography
                            variant="body2"
                            style={{ color: "#95a5a6", fontStyle: "italic" }}
                        >
                            {tabValue === 0
                                ? `${produtosFiltrados.length} produtos encontrados`
                                : `${opcoesFiltradas.length} opções encontradas`}
                        </Typography>
                        <Typography variant="body2" style={{ color: "#34495e", fontWeight: 600 }}>
                            {tabValue === 0
                                ? `Selecionados: ${fichaParaVincular?.produtos?.length || 0}`
                                : `Selecionadas: ${fichaParaVincular?.opcoes?.length || 0}`}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: 16 }}>
                    <Button
                        onClick={() => {
                            setOpenDialogProdutos(false)
                            setBuscaProdutos("")
                            setBuscaOpcoes("")
                            setFiltroCategorias([])
                        }}
                        style={{ borderRadius: 15 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => {
                            vincularProdutos()
                            setOpenDialogProdutos(false)
                        }}
                        variant="contained"
                        style={{ borderRadius: 15, backgroundColor: "#4caf50", color: "white" }}
                    >
                        Salvar Vinculações
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
